import React, { useEffect, useMemo, useState } from 'react';
import { geometryLessonSections, LessonSection } from '../data/geometryLessons';
import { useSubscription } from '../hooks/useSubscription';
import PaywallModal from './PaywallModal';

const STORAGE_KEY = 'geometry-learning-progress-v1';

type CompletionMap = Record<string, boolean>;
type AnswerMap = Record<string, number | undefined>;

const GeometryLearning: React.FC = () => {
  const { isPro } = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState<string>(geometryLessonSections[0]?.id ?? '');
  const [completedSections, setCompletedSections] = useState<CompletionMap>({});
  const [selectedAnswers, setSelectedAnswers] = useState<AnswerMap>({});

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
      if (stored.completed) {
        setCompletedSections(stored.completed);
      }
      if (stored.answers) {
        setSelectedAnswers(stored.answers);
      }
    } catch (error) {
      console.warn('Unable to parse stored geometry learning progress', error);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const payload = JSON.stringify({ completed: completedSections, answers: selectedAnswers });
    window.localStorage.setItem(STORAGE_KEY, payload);
  }, [completedSections, selectedAnswers]);

  const activeSection: LessonSection | undefined = useMemo(() => {
    return geometryLessonSections.find(section => section.id === activeSectionId) ?? geometryLessonSections[0];
  }, [activeSectionId]);

  const completionRate = useMemo(() => {
    if (!geometryLessonSections.length) return 0;
    const completedCount = geometryLessonSections.filter(section => completedSections[section.id]).length;
    return Math.round((completedCount / geometryLessonSections.length) * 100);
  }, [completedSections]);

  const handleSelectAnswer = (section: LessonSection, choiceIndex: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [section.conceptCheck.id]: choiceIndex
    }));
  };

  const toggleCompletion = (sectionId: string) => {
    setCompletedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  if (!activeSection) {
    return (
      <div
        style={{
          padding: '24px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '24px',
          border: '1px solid var(--color-border)'
        }}
      >
        <p style={{ margin: 0 }}>No lesson content available yet. Please add geometry sections to continue.</p>
      </div>
    );
  }

  const selectedChoice = selectedAnswers[activeSection.conceptCheck.id];
  const isCorrect = selectedChoice === activeSection.conceptCheck.answerIndex;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', color: 'var(--color-text)' }}>
      <div
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '28px',
          padding: '24px',
          border: '1px solid var(--color-border)',
          boxShadow: 'var(--shadow-card)'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <p style={{ margin: 0, textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: '12px', color: 'var(--color-text-muted)' }}>
              Guided Quant Learning
            </p>
            <h2 style={{ margin: '4px 0 0', fontSize: '24px' }}>Geometry Game Plan</h2>
            <p style={{ margin: '8px 0 0', color: 'var(--color-text-muted)' }}>
              Adapted from the GRE Math Review PDF so you can study the strategies before jumping into problem drills.
            </p>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Lesson progress</span>
              <strong>{completionRate}% complete</strong>
            </div>
            <div
              style={{
                height: '10px',
                borderRadius: '999px',
                backgroundColor: 'var(--color-surface-muted)'
              }}
            >
              <div
                style={{
                  width: `${completionRate}%`,
                  height: '100%',
                  borderRadius: '999px',
                  background: 'linear-gradient(90deg, var(--color-primary), #5b8def)',
                  transition: 'width 0.3s ease'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(220px, 280px) 1fr',
          gap: '20px',
          alignItems: 'flex-start'
        }}
      >
        <aside
          style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '24px',
            padding: '16px',
            border: '1px solid var(--color-border)'
          }}
        >
          <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '14px', color: 'var(--color-text-muted)' }}>Lesson outline</p>
          {showPaywall && <PaywallModal feature="All Geometry sections" onClose={() => setShowPaywall(false)} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {geometryLessonSections.map((section, index) => {
              const isActive = section.id === activeSection.id;
              const completed = completedSections[section.id];
              const locked = !isPro && index >= 2;
              return (
                <button
                  key={section.id}
                  onClick={() => { if (locked) { setShowPaywall(true); } else { setActiveSectionId(section.id); } }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: '1px solid',
                    borderColor: isActive ? 'var(--color-primary)' : 'var(--color-border)',
                    borderRadius: '18px',
                    padding: '12px',
                    backgroundColor: isActive ? 'var(--color-success-light)' : 'var(--color-surface)',
                    color: locked ? 'var(--color-text-muted)' : 'var(--color-text)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: locked ? 'default' : 'pointer',
                    opacity: locked ? 0.6 : 1,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: locked ? 'var(--color-border)' : completed ? 'var(--color-success, #10b981)' : 'var(--color-border)',
                      flexShrink: 0
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <strong style={{ fontSize: '14px' }}>{section.title}</strong>
                    <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{section.headline}</span>
                  </div>
                  {locked && <span style={{ fontSize: 13 }}>🔒</span>}
                </button>
              );
            })}
          </div>
        </aside>

        <section
          style={{
            backgroundColor: 'var(--color-surface)',
            borderRadius: '28px',
            padding: '24px',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-card)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start' }}>
            <div>
              <p style={{ margin: 0, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '12px', color: 'var(--color-text-muted)' }}>
                {activeSection.reference}
              </p>
              <h3 style={{ margin: '6px 0 4px', fontSize: '22px' }}>{activeSection.title}</h3>
              <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>{activeSection.summary}</p>
            </div>
            <button
              onClick={() => toggleCompletion(activeSection.id)}
              style={{
                padding: '10px 18px',
                borderRadius: '999px',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                backgroundColor: completedSections[activeSection.id] ? 'var(--color-success, #10b981)' : 'var(--color-primary)',
                color: '#fff',
                flexShrink: 0
              }}
            >
              {completedSections[activeSection.id] ? 'Mark for Review' : 'Mark Complete'}
            </button>
          </div>

          <div style={{ marginTop: '20px', display: 'grid', gap: '18px' }}>
            <div
              style={{
                backgroundColor: 'var(--color-surface-muted)',
                borderRadius: '20px',
                padding: '16px'
              }}
            >
              <h4 style={{ margin: '0 0 8px', fontSize: '16px' }}>Big ideas</h4>
              <ul style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text)' }}>
                {activeSection.bigIdeas.map(point => (
                  <li key={point} style={{ marginBottom: '6px', lineHeight: 1.5 }}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div
              style={{
                backgroundColor: 'var(--color-surface-muted)',
                borderRadius: '20px',
                padding: '16px'
              }}
            >
              <h4 style={{ margin: '0 0 8px', fontSize: '16px' }}>Action drills</h4>
              <ol style={{ margin: 0, paddingLeft: '18px', color: 'var(--color-text)' }}>
                {activeSection.actionSteps.map(step => (
                  <li key={step} style={{ marginBottom: '6px', lineHeight: 1.5 }}>
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            <div
              style={{
                borderRadius: '20px',
                padding: '16px',
                border: '1px solid var(--color-border)'
              }}
            >
              <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Concept check</p>
              <p style={{ margin: '0 0 12px', color: 'var(--color-text-muted)' }}>{activeSection.conceptCheck.prompt}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {activeSection.conceptCheck.choices.map((choice, index) => {
                  const isSelected = selectedChoice === index;
                  let background = 'var(--color-surface)';
                  let border = 'var(--color-border)';
                  if (isSelected) {
                    background = isCorrect ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)';
                    border = isCorrect ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)';
                  }
                  return (
                    <button
                      key={choice}
                      onClick={() => handleSelectAnswer(activeSection, index)}
                      style={{
                        borderRadius: '16px',
                        border: `1px solid ${border}`,
                        backgroundColor: background,
                        padding: '10px 14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: 'var(--color-text)'
                      }}
                    >
                      {choice}
                    </button>
                  );
                })}
              </div>
              {selectedChoice !== undefined && (
                <div
                  style={{
                    marginTop: '12px',
                    padding: '12px',
                    borderRadius: '16px',
                    backgroundColor: isCorrect ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                    color: isCorrect ? '#047857' : '#dc2626'
                  }}
                >
                  {isCorrect ? 'Correct!' : 'Keep reviewing.'} {activeSection.conceptCheck.explanation}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default GeometryLearning;

